import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { overlayVariants, sheetVariants, transition } from '../lib/motion.js';
import { useEffect, useRef, useState } from 'react';
import { setDisplayName } from '../lib/profile.js';
import { supabase } from '../lib/supabase.js';
import { claimAccountWithEmail } from '../lib/barIdentity.js';

/**
 * Modale « compte » : invité anonyme (défaut), personnalisation du pseudo,
 * inscription / connexion par e-mail (magic link Supabase).
 */
export default function AccountModal({
  open,
  onClose,
  defaultView = 'home',
  barUser,
  authEmail,
  barAuthReady,
  barAuthReason,
  barAuthProfileError,
  updateBarUsername,
  initBarIdentity,
  initialPseudo = '',
  paidQueueUiEnabled = true,
  onOpenProfile,
}) {
  const [view, setView] = useState('home');
  const [pseudo, setPseudo] = useState('');
  const [pseudoBusy, setPseudoBusy] = useState(false);
  const [pseudoErr, setPseudoErr] = useState(null);

  const [emailIn, setEmailIn] = useState('');
  const [emailPwd, setEmailPwd] = useState('');
  const [authMode, setAuthMode] = useState('signin'); // 'signin' | 'signup'
  const [emailBusy, setEmailBusy] = useState(false);
  const [emailMsg, setEmailMsg] = useState(null);
  const [emailErr, setEmailErr] = useState(null);

  const [homeBusy, setHomeBusy] = useState(false);
  const [homeNotice, setHomeNotice] = useState(null);

  const pseudoRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setView(defaultView);
    setPseudo(initialPseudo ?? '');
    setPseudoErr(null);
    setPseudoBusy(false);
    setEmailIn(authEmail ?? '');
    setEmailPwd('');
    setAuthMode('signin');
    setEmailMsg(null);
    setEmailErr(null);
    setEmailBusy(false);
    setHomeBusy(false);
    setHomeNotice(null);
    const id = window.setTimeout(() => {
      if (defaultView === 'pseudo') pseudoRef.current?.focus();
    }, 60);
    return () => window.clearTimeout(id);
  }, [open, defaultView, initialPseudo, authEmail]);

  // Escape ferme la modale (a11y clavier / lecteur d'écran).
  useEffect(() => {
    if (!open) return undefined;
    function onKey(e) {
      if (e.key === 'Escape') onClose?.();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  async function submitPseudo(e) {
    e.preventDefault();
    const trimmed = pseudo.trim();
    if (trimmed.length < 1) {
      setPseudoErr('Choisis au moins un caractère.');
      return;
    }
    setPseudoErr(null);
    setPseudoBusy(true);
    const res = await updateBarUsername(trimmed);
    setPseudoBusy(false);
    if (!res.ok) {
      setPseudoErr(res.message ?? 'Erreur.');
      return;
    }
    setDisplayName(trimmed);
    setView('home');
  }

  async function submitEmailAuth(e) {
    e.preventDefault();
    const email = emailIn.trim().toLowerCase();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailErr('Indique une adresse e-mail valide.');
      return;
    }
    if (!emailPwd || emailPwd.length < 8) {
      setEmailErr('Le mot de passe doit faire au moins 8 caractères.');
      return;
    }
    setEmailErr(null);
    setEmailMsg(null);
    setEmailBusy(true);

    let error = null;
    let linked = false;
    if (authMode === 'signup') {
      // Lie l'e-mail au compte invité courant (garde pseudo + progression).
      const res = await claimAccountWithEmail(email, emailPwd);
      error = res.error;
      linked = res.linked;
    } else {
      ({ error } = await supabase.auth.signInWithPassword({ email, password: emailPwd }));
    }
    setEmailBusy(false);

    if (error) {
      const m = error.message ?? 'Erreur.';
      if (authMode === 'signup' && /already|registered|exist|taken|in use/i.test(m)) {
        setEmailErr('Cet e-mail a déjà un compte. Choisis « J’ai déjà un compte » pour te connecter.');
      } else {
        setEmailErr(m);
      }
      return;
    }
    // Rafraîchit barUser + authEmail tout de suite : ne pas dépendre du seul
    // onAuthStateChange, dont le timing (lock auth navigator.locks) peut retarder
    // l'update et laisser le front sur le formulaire de connexion. Le single-flight
    // de initBarIdentity dédoublonne avec l'appel déclenché par onAuthStateChange.
    await initBarIdentity?.();
    setEmailMsg(
      authMode === 'signup'
        ? linked
          ? 'Compte créé — ton pseudo et ta progression sont conservés.'
          : 'Compte créé. Tu es connecté sur cet appareil.'
        : 'Connecté. Tu retrouves ta progression sur cet appareil.'
    );
    setView('home');
  }

  async function handleLogout() {
    setHomeBusy(true);
    setHomeNotice(null);
    // scope 'local' : ne déconnecte que cet appareil (garde les autres sessions).
    await supabase.auth.signOut({ scope: 'local' });
    // Recrée une identité invité anonyme pour continuer à participer (RLS exige
    // une session). Rafraîchit barUser + authEmail (→ null) sans attendre onAuthStateChange.
    await initBarIdentity?.();
    setHomeBusy(false);
    setHomeNotice('Déconnecté. Tu participes de nouveau en invité sur cet appareil.');
  }

  const titleHome = 'Ton compte pour cette soirée';
  const canUsePseudo = Boolean(barUser);
  const isLinked = Boolean(authEmail);
  const reduceMotion = useReducedMotion();

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-x-0 top-0 z-[80] flex h-[100dvh] items-end justify-center bg-black/75 backdrop-blur-md p-0 pt-[env(safe-area-inset-top)] sm:items-center sm:p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="account-modal-title"
          variants={overlayVariants}
          initial={reduceMotion ? false : 'initial'}
          animate="animate"
          exit={reduceMotion ? undefined : 'exit'}
          transition={transition(reduceMotion, 'fast')}
          onClick={() => onClose?.()}
        >
          <motion.div
            className="sheet-surface max-h-[min(92dvh,720px)] w-full max-w-[480px] overflow-y-auto rounded-t-3xl border border-border p-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] sm:rounded-2xl sm:pb-5"
            variants={sheetVariants}
            initial={reduceMotion ? false : 'initial'}
            animate="animate"
            exit={reduceMotion ? undefined : 'exit'}
            transition={transition(reduceMotion, 'panel')}
            onClick={(e) => e.stopPropagation()}
          >
            {view === 'home' && (
              <>
                <h2 id="account-modal-title" className="text-lg font-bold">
                  {isLinked ? 'Ton compte' : titleHome}
                </h2>
                <p className="mt-2 text-sm text-white/58">
                  {isLinked ? (
                    'Ton pseudo et ta progression te suivent sur tous tes appareils.'
                  ) : (
                    <>
                      Participe <strong className="text-white/85">en invité</strong> sans
                      inscription, personnalise ton pseudo, ou{' '}
                      <strong className="text-white/85">crée un compte</strong> pour retrouver ta
                      progression sur un autre appareil.
                    </>
                  )}
                </p>

                {!barAuthReady ? (
                  <p className="mt-4 rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-xs text-white/60">
                    Préparation de la session…
                  </p>
                ) : null}

                {barAuthReady && barUser ? (
                  isLinked ? (
                    <div className="mt-4 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 px-3 py-3">
                      <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-widest text-emerald-200/95">
                        <span aria-hidden>✓</span> Compte connecté
                      </p>
                      <p className="mt-1 text-sm font-semibold text-white">{barUser.username}</p>
                      <p className="mt-0.5 text-xs text-white/55">{authEmail}</p>
                    </div>
                  ) : (
                    <div className="mt-4 rounded-2xl border border-white/12 bg-white/[0.06] px-3 py-3">
                      <p className="text-[10px] font-semibold uppercase tracking-widest text-white/45">
                        Mode invité
                      </p>
                      <p className="mt-1 text-sm font-semibold text-white">{barUser.username}</p>
                      <p className="mt-1 text-xs text-white/55">
                        {paidQueueUiEnabled
                          ? 'Compte anonyme sur cet appareil — tu participes déjà pleinement (votes, suggestions et paliers collectifs).'
                          : 'Compte anonyme sur cet appareil — tu participes déjà pleinement (votes et suggestions).'}
                      </p>
                    </div>
                  )
                ) : null}

                {homeNotice ? (
                  <p className="mt-4 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-200">
                    {homeNotice}
                  </p>
                ) : null}

                {barAuthReady && !barUser && barAuthReason ? (
                  <div className="mt-4 rounded-2xl border border-amber-500/35 bg-amber-500/10 px-3 py-3">
                    <p className="text-xs font-semibold text-amber-100">Connexion locale impossible</p>
                    <p className="mt-1 text-[11px] text-amber-200/85">
                      {barAuthReason === 'no_auth_session'
                        ? 'Active « Sign in anonymously » dans Supabase, ou connecte-toi par e-mail ci-dessous.'
                        : 'Vérifie les droits sur la table bar_users (INSERT / RLS) ou réessaie.'}
                    </p>
                    {barAuthProfileError ? (
                      <p className="mt-2 rounded-lg bg-black/30 px-2 py-1.5 font-mono text-[10px] text-amber-50/90">
                        {barAuthProfileError}
                      </p>
                    ) : null}
                    <button
                      type="button"
                      className="btn-secondary mt-3 w-full py-2.5 text-xs"
                      onClick={() => void initBarIdentity()}
                    >
                      Réessayer la connexion invité
                    </button>
                  </div>
                ) : null}

                <div className="mt-5 flex flex-col gap-2.5">
                  {onOpenProfile && canUsePseudo ? (
                    <button
                      type="button"
                      className="btn-secondary w-full justify-center py-3 text-sm"
                      onClick={() => {
                        onClose?.();
                        onOpenProfile();
                      }}
                    >
                      🏆 Mon profil &amp; mes badges
                    </button>
                  ) : null}

                  <button
                    type="button"
                    className="btn-secondary w-full justify-center py-3 text-sm"
                    onClick={() => setView('pseudo')}
                    disabled={!canUsePseudo}
                  >
                    Personnaliser mon pseudo
                  </button>
                  {!canUsePseudo && barAuthReady ? (
                    <p className="text-center text-[10px] text-white/38">
                      Pseudo disponible après une connexion invité réussie.
                    </p>
                  ) : null}

                  {isLinked ? (
                    <button
                      type="button"
                      className="btn-secondary w-full justify-center py-3 text-sm"
                      onClick={handleLogout}
                      disabled={homeBusy}
                    >
                      {homeBusy ? 'Déconnexion…' : 'Se déconnecter'}
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn-primary w-full justify-center py-3 text-sm"
                      onClick={() => setView('email')}
                    >
                      Créer un compte / me connecter
                    </button>
                  )}

                  <button
                    type="button"
                    className="btn-ghost w-full justify-center py-2.5 text-xs text-white/55"
                    onClick={() => onClose?.()}
                  >
                    {isLinked ? 'Fermer' : 'Rester invité — fermer'}
                  </button>
                </div>

                {!isLinked ? (
                  <div className="mt-6 rounded-xl border border-white/10 bg-black/25 px-3 py-2.5">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-white/38">
                      Bientôt
                    </p>
                    <button
                      type="button"
                      disabled
                      className="btn-secondary mt-2 w-full opacity-45"
                      title="Bientôt disponible"
                    >
                      Continuer avec Google
                    </button>
                  </div>
                ) : null}
              </>
            )}

            {view === 'pseudo' && (
              <>
                <div className="flex items-center gap-2">
                  <button type="button" className="btn-ghost shrink-0 px-2 py-1 text-xs" onClick={() => setView('home')}>
                    ← Retour
                  </button>
                  <h2 className="text-lg font-bold">Pseudo affiché</h2>
                </div>
                <p className="mt-2 text-sm text-white/58">
                  {paidQueueUiEnabled
                    ? 'Un coup de clavier pour le live (pseudo, classements). Pas d’obligation avant de participer si tu gardes ton nom invité automatique.'
                    : 'Un coup de clavier pour le live (pseudo affiché sur les suggestions). Pas d’obligation avant de participer si tu gardes ton nom invité automatique.'}
                </p>

                <form onSubmit={submitPseudo} className="mt-4 flex flex-col gap-3">
                  <input
                    ref={pseudoRef}
                    className="input"
                    value={pseudo}
                    onChange={(e) => setPseudo(e.target.value)}
                    placeholder="Ton pseudo"
                    maxLength={32}
                  />
                  <p className="text-right text-[10px] text-white/35">{pseudo.trim().length}/32</p>
                  {pseudoErr ? (
                    <p className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-300">
                      {pseudoErr}
                    </p>
                  ) : null}
                  <button type="submit" disabled={pseudoBusy || !canUsePseudo} className="btn-primary py-3 text-sm">
                    {pseudoBusy ? 'Enregistrement…' : 'Enregistrer le pseudo'}
                  </button>
                </form>
              </>
            )}

            {view === 'email' && (
              <>
                <div className="flex items-center gap-2">
                  <button type="button" className="btn-ghost shrink-0 px-2 py-1 text-xs" onClick={() => setView('home')}>
                    ← Retour
                  </button>
                  <h2 className="text-lg font-bold">E-mail et mot de passe</h2>
                </div>
                <p className="mt-2 text-sm text-white/58">
                  Crée un compte ou connecte-toi pour retrouver ton pseudo et ta progression sur un
                  autre appareil.
                </p>

                <div className="mt-3 flex gap-2">
                  <button
                    type="button"
                    className={`flex-1 rounded-lg border px-2 py-1.5 text-xs ${
                      authMode === 'signin'
                        ? 'border-accent/45 bg-bg-soft text-text'
                        : 'border-border text-white/60'
                    }`}
                    onClick={() => setAuthMode('signin')}
                  >
                    J&apos;ai déjà un compte
                  </button>
                  <button
                    type="button"
                    className={`flex-1 rounded-lg border px-2 py-1.5 text-xs ${
                      authMode === 'signup'
                        ? 'border-accent/45 bg-bg-soft text-text'
                        : 'border-border text-white/60'
                    }`}
                    onClick={() => setAuthMode('signup')}
                  >
                    Créer un compte
                  </button>
                </div>

                <form onSubmit={submitEmailAuth} className="mt-4 flex flex-col gap-3">
                  <input
                    className="input"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    placeholder="toi@mail.com"
                    value={emailIn}
                    onChange={(e) => setEmailIn(e.target.value)}
                    required
                  />
                  <input
                    className="input"
                    type="password"
                    autoComplete={authMode === 'signup' ? 'new-password' : 'current-password'}
                    placeholder="Mot de passe (8 caractères min.)"
                    value={emailPwd}
                    onChange={(e) => setEmailPwd(e.target.value)}
                    minLength={8}
                    required
                  />
                  {emailErr ? (
                    <p className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-300">
                      {emailErr}
                    </p>
                  ) : null}
                  {emailMsg ? (
                    <p className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-200">
                      {emailMsg}
                    </p>
                  ) : null}
                  <button type="submit" disabled={emailBusy} className="btn-primary py-3 text-sm">
                    {emailBusy
                      ? authMode === 'signup'
                        ? 'Création…'
                        : 'Connexion…'
                      : authMode === 'signup'
                        ? 'Créer mon compte'
                        : 'Se connecter'}
                  </button>
                </form>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
